import { Box, SimpleGrid, Stack, Group, Progress, RingProgress, Table } from '@mantine/core';
import {
  IconUsers,
  IconEye,
  IconClock,
  IconArrowBounce,
  IconTrendingUp,
  IconTrendingDown,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Card, Text, Title, Button } from '../../../../components/ui';
import {
  analyticsStats,
  trafficSources,
  topPages,
  deviceBreakdown,
  realtimeStats,
} from '../mockData/analytics';

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US').format(value);
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const StatCard = ({
  title,
  value,
  change,
  icon: Icon,
  color,
  format = 'number'
}: {
  title: string;
  value: number;
  change: number;
  icon: React.ElementType;
  color: string;
  format?: 'number' | 'duration' | 'percent';
}) => {
  const displayValue = format === 'duration'
    ? formatDuration(value)
    : format === 'percent'
      ? `${value}%`
      : formatNumber(value);

  return (
    <Card>
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed">{title}</Text>
        <Box
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            backgroundColor: `var(--mantine-color-${color}-light)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
        </Box>
      </Group>
      <Text size="xl" fw={700}>{displayValue}</Text>
      <Group gap="xs" mt="xs">
        {change >= 0 ? (
          <IconTrendingUp size={16} color="var(--mantine-color-green-filled)" />
        ) : (
          <IconTrendingDown size={16} color="var(--mantine-color-red-filled)" />
        )}
        <Text size="xs" c={change >= 0 ? 'green' : 'red'}>
          {change >= 0 ? '+' : ''}{change}%
        </Text>
        <Text size="xs" c="dimmed">vs last period</Text>
      </Group>
    </Card>
  );
};

export default function AnalyticsDashboardPage() {
  const navigate = useNavigate();
  const totalTraffic = trafficSources.reduce((acc, s) => acc + s.visitors, 0);
  const totalSessions = deviceBreakdown.reduce((acc, d) => acc + d.sessions, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Analytics Dashboard</Title>
          <Text c="dimmed" size="sm">Track your website performance and user behavior</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('audience')}>View Audience</Button>
          <Button onClick={() => navigate('reports')}>View Reports</Button>
        </Group>
      </Group>

      {/* Realtime Stats Bar */}
      <Card>
        <Group justify="space-between">
          <Group gap="xl">
            <Box>
              <Text size="xs" c="dimmed">Active Now</Text>
              <Text size="lg" fw={700} c="green">{realtimeStats.activeUsers}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Page Views (Today)</Text>
              <Text size="lg" fw={700}>{formatNumber(realtimeStats.pageViews)}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Events (Today)</Text>
              <Text size="lg" fw={700}>{formatNumber(realtimeStats.events)}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Avg Load Time</Text>
              <Text size="lg" fw={700}>{realtimeStats.avgLoadTime}s</Text>
            </Box>
          </Group>
          <Box
            p="xs"
            style={{
              backgroundColor: 'var(--mantine-color-green-light)',
              borderRadius: 8,
            }}
          >
            <Text size="xs" fw={500} c="green">Live</Text>
          </Box>
        </Group>
      </Card>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Visitors"
          value={analyticsStats.totalVisitors}
          change={analyticsStats.visitorsChange}
          icon={IconUsers}
          color="blue"
        />
        <StatCard
          title="Page Views"
          value={analyticsStats.pageViews}
          change={analyticsStats.pageViewsChange}
          icon={IconEye}
          color="green"
        />
        <StatCard
          title="Avg. Session Duration"
          value={analyticsStats.avgSessionDuration}
          change={analyticsStats.sessionDurationChange}
          icon={IconClock}
          color="violet"
          format="duration"
        />
        <StatCard
          title="Bounce Rate"
          value={analyticsStats.bounceRate}
          change={analyticsStats.bounceRateChange}
          icon={IconArrowBounce}
          color="orange"
          format="percent"
        />
      </SimpleGrid>

      {/* Traffic Sources and Devices */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Traffic Sources */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Traffic Sources</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('audience')}
            >
              View Details
            </Button>
          </Group>
          <Stack gap="sm">
            {trafficSources.map(source => (
              <Group key={source.source} justify="space-between">
                <Group gap="sm" style={{ flex: 1 }}>
                  <Box
                    w={12}
                    h={12}
                    style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${source.color}-filled)` }}
                  />
                  <Text size="sm">{source.source}</Text>
                </Group>
                <Group gap="sm" style={{ flex: 1 }}>
                  <Progress
                    value={source.percentage}
                    size="sm"
                    radius="xl"
                    style={{ flex: 1 }}
                    color={source.color}
                  />
                </Group>
                <Group gap="sm" w={120} justify="flex-end">
                  <Text size="sm" c="dimmed">{source.percentage}%</Text>
                  <Text size="sm" fw={500}>{formatNumber(source.visitors)}</Text>
                </Group>
              </Group>
            ))}
          </Stack>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <Text fw={600} mb="md">Device Breakdown</Text>
          <Group align="center" gap="xl">
            <RingProgress
              size={140}
              thickness={16}
              sections={deviceBreakdown.map(d => ({
                value: d.percentage,
                color: d.color,
              }))}
              label={
                <Box ta="center">
                  <Text size="xl" fw={700}>{formatNumber(totalSessions)}</Text>
                  <Text size="xs" c="dimmed">Sessions</Text>
                </Box>
              }
            />
            <Stack gap="sm" style={{ flex: 1 }}>
              {deviceBreakdown.map(device => (
                <Group key={device.device} justify="space-between">
                  <Group gap="sm">
                    <Box
                      w={12}
                      h={12}
                      style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${device.color}-filled)` }}
                    />
                    <Text size="sm">{device.device}</Text>
                  </Group>
                  <Group gap="md">
                    <Text size="sm" c="dimmed">{device.percentage}%</Text>
                    <Text size="sm" fw={500}>{formatNumber(device.sessions)}</Text>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Top Pages */}
      <Card>
        <Group justify="space-between" mb="md">
          <Text fw={600}>Top Pages</Text>
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate('reports')}
          >
            View All
          </Button>
        </Group>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Page</Table.Th>
              <Table.Th ta="right">Views</Table.Th>
              <Table.Th ta="right">Avg. Time</Table.Th>
              <Table.Th ta="right">Bounce Rate</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {topPages.slice(0, 5).map(page => (
              <Table.Tr key={page.page}>
                <Table.Td>
                  <Text size="sm" fw={500}>{page.page}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">{formatNumber(page.views)}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">{formatDuration(page.avgTime)}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" c={page.bounceRate > 50 ? 'red' : page.bounceRate > 35 ? 'yellow' : 'green'}>
                    {page.bounceRate}%
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
